export const TIER_ORDER = ['free', 'basic', 'pro', 'biz'];

export const TIERS = {
  basic: {
    id: 'basic',
    interval: 'month',
    label: 'Freelancer Basic',
    price: 1.99,
  },
  biz: {
    id: 'biz',
    interval: 'month',
    label: 'Business',
    price: 9.99, // starting price, see priceForSeats(): graduated pricing, scripts/stripe_setup.py (business_per_seat_monthly)
  },
  free: {
    id: 'free',
    interval: null,
    label: 'Free · Local',
    price: null,
  },
  pro: {
    id: 'pro',
    interval: 'month',
    label: 'Freelancer Pro',
    price: 4.99,
  },
};

// Business is graduated, not flat-per-seat: $9.99 covers the first BIZ_SEAT_BREAK seats,
// then BIZ_PER_SEAT_AFTER per seat beyond that. Mirrors the Stripe Price tiers in
// scripts/stripe_setup.py, keep both in sync if this ever changes.
export const BIZ_SEAT_BREAK = 5;
export const BIZ_BASE_PRICE = 9.99;
export const BIZ_PER_SEAT_AFTER = 1;

/** Total monthly price for a given tier id and seat count (seat count only matters for 'biz'). */
export function priceForSeats(tierId, seats = 1) {
  const price = TIERS[tierId]?.price;
  if (price == null) return null;
  if (tierId !== 'biz') return price;
  const extraSeats = Math.max(0, seats - BIZ_SEAT_BREAK);
  return BIZ_BASE_PRICE + extraSeats * BIZ_PER_SEAT_AFTER;
}

// Each feature unlocks at 'tier' and all tiers above it.
export const FEATURES = [
  // ── Available on all tiers ──────────────────────────────────────────
  {
    desc: 'Live clock-in with ISO 8601 timestamps',
    id: 'time_tracking',
    label: 'Punch in / punch out',
    tier: 'free',
  },
  {
    desc: 'Back-fill entries with clock-in / clock-out times',
    id: 'manual_entry',
    label: 'Manual time entry',
    tier: 'free',
  },
  {
    desc: 'Tamper-proof PDF export of hours and entries',
    id: 'timesheet_export',
    label: 'Timesheet report',
    tier: 'free',
  },
  {
    desc: 'Zero-knowledge ECDSA JWS seal on every batch',
    id: 'crypto_sign',
    label: 'Cryptographic signing',
    tier: 'free',
  },
  {
    desc: 'On-device ML scan of pay stubs, timesheets, punch cards, schedules',
    id: 'ocr_scan',
    label: 'Document scanning (OCR)',
    tier: 'free',
  },
  {
    desc: 'All data lives on-device; syncs to your Google Drive or OneDrive',
    id: 'offline_sync',
    label: 'Offline-first storage',
    tier: 'free',
  },
  {
    desc: 'Unlimited local invoices; hosted shareable links are capped on the free tier',
    id: 'invoices_5',
    label: 'Up to 5 hosted invoice links / month',
    tier: 'free',
  },
  {
    desc: 'Daily and weekly OT flagged per FLSA and state rules',
    id: 'ot_detection',
    label: 'Overtime detection',
    tier: 'free',
  },
  {
    desc: 'Variance display; predictive scheduling violation flags',
    id: 'schedule_track',
    label: 'Schedule vs. actual tracking',
    tier: 'free',
  },

  // ── Freelancer Basic ────────────────────────────────────────────────
  {
    desc: 'Hosted on Cloudflare R2 with a shareable URL',
    id: 'invoices_45',
    label: 'Up to 45 hosted invoice links / month',
    tier: 'basic',
  },
  {
    desc: 'pl.ddproxy.com/invoice/:id, no file attachments needed',
    id: 'invoice_hosting',
    label: 'Hosted invoice links',
    tier: 'basic',
  },
  {
    desc: 'Know when a client views your invoice',
    id: 'invoice_open',
    label: 'Invoice open tracking',
    tier: 'basic',
  },
  {
    desc: 'Send invoices directly from the app via Cloudflare Email',
    id: 'email_delivery',
    label: 'Email invoice delivery',
    tier: 'basic',
  },

  // ── Freelancer Pro ──────────────────────────────────────────────────
  { desc: '', id: 'invoices_60', label: 'Up to 60 hosted invoice links / month', tier: 'pro' },
  {
    desc: 'Track hours and invoices across multiple clients in one view',
    id: 'multi_client',
    label: 'Multi-client dashboard',
    tier: 'pro',
  },
  {
    desc: 'HMAC-SHA256 signed events to Zapier, Make, or custom endpoints',
    id: 'webhooks',
    label: 'Webhook automation',
    tier: 'pro',
  },
  {
    desc: '3 attempts with exponential backoff via Cloudflare Queues',
    id: 'webhook_retry',
    label: 'Webhook retry queue',
    tier: 'pro',
  },

  // ── Business ────────────────────────────────────────────────────────
  {
    desc: 'Multi-tenant separation; admin vs. member roles',
    id: 'teams',
    label: 'Team management',
    tier: 'biz',
  },
  {
    desc: 'Manage team seats via Stripe Customer Portal',
    id: 'seat_billing',
    label: 'Seat-based billing',
    tier: 'biz',
  },
  {
    desc: 'Outbound SMS via the native device, no Twilio cost',
    id: 'sms_alerts',
    label: 'SMS notifications',
    tier: 'biz',
  },
];

/** Returns true if userTier unlocks featureId. */
export function hasFeature(userTier, featureId) {
  const feature = FEATURES.find(f => f.id === featureId);
  if (!feature) return false;
  return TIER_ORDER.indexOf(userTier) >= TIER_ORDER.indexOf(feature.tier);
}

/** All features available at a given tier (inclusive of lower tiers). */
export function featuresForTier(tier) {
  const rank = TIER_ORDER.indexOf(tier);
  return FEATURES.filter(f => TIER_ORDER.indexOf(f.tier) <= rank);
}

/** Features grouped by tier, for display in settings. */
export function featuresByTier() {
  return TIER_ORDER.map(id => ({
    features: FEATURES.filter(f => f.tier === id),
    tier: TIERS[id],
  }));
}

/**
 * Comparison matrix for the plan table.
 * Each row cell: true = included (checkmark), false = not included (✗), string = custom label.
 */
export const FEATURE_GROUPS = [
  {
    label: 'Time Tracking',
    rows: [
      { basic: true, biz: true, free: true, label: 'Punch in / punch out', pro: true },
      { basic: true, biz: true, free: true, label: 'Manual time entry', pro: true },
      { basic: true, biz: true, free: true, label: 'Overtime detection (FLSA)', pro: true },
      { basic: true, biz: true, free: true, label: 'Schedule vs. actual tracking', pro: true },
    ],
  },
  {
    label: 'Compliance & Export',
    rows: [
      { basic: true, biz: true, free: true, label: 'Timesheet report PDF', pro: true },
      { basic: true, biz: true, free: true, label: 'Cryptographic signing (ECDSA)', pro: true },
      { basic: true, biz: true, free: true, label: 'Document scanning (OCR)', pro: true },
      { basic: true, biz: true, free: true, label: 'Offline-first storage', pro: true },
    ],
  },
  {
    label: 'Invoicing',
    rows: [
      { basic: '45', biz: '∞', free: '5', label: 'Hosted invoice links / month', pro: '60' },
      { basic: true, biz: true, free: false, label: 'Hosted invoice links', pro: true },
      { basic: true, biz: true, free: false, label: 'Invoice open tracking', pro: true },
      { basic: true, biz: true, free: false, label: 'Email invoice delivery', pro: true },
    ],
  },
  {
    label: 'Automation',
    rows: [
      { basic: false, biz: true, free: false, label: 'Multi-client dashboard', pro: true },
      { basic: false, biz: true, free: false, label: 'Webhook automation', pro: true },
      { basic: false, biz: true, free: false, label: 'Webhook retry queue', pro: true },
    ],
  },
  {
    label: 'Business',
    rows: [
      { basic: false, biz: true, free: false, label: 'Team management', pro: false },
      { basic: false, biz: true, free: false, label: 'Seat-based billing', pro: false },
      { basic: false, biz: true, free: false, label: 'SMS notifications', pro: false },
    ],
  },
];
